import {
  CustomButton,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomModal,
  CustomSelect,
  Option,
  Text,
  useText,
} from '@src/components';
import modalClose from '@src/assets/icons/icon-close.svg';
import '@src/styles/balanceUpload/excelUploadDeleteModal.scss';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import infoButton from '@src/assets/icons/icon-info.svg';
import {
  excelUploadPrimaryColumn,
  loadBalanceDetailExcelUpload,
} from '@src/store/slice/balanceUploadDetailSlice';
import { Form, Table } from 'antd';
import { setShowLoader, setSpinText } from '@src/store/slice/appSettingsSlice';
import PropTypes from 'prop-types';
import { VouIdCode } from '@src/store/enums';

const alphabets = ' ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const ExcelUploadColumnChoice2 = ({
  modalVisible,
  handleModalVisible,
  excelFile,
  excelParams,
  afterUpload,
  decodedExcelFile,
  isCompliments = false,
  isMarket = false,
}) => {
  const dispatch = useDispatch();
  const chooseText = useText('choose');
  const monthlyAmountText = useText('monthlyAmount');
  const dailyAmountText = useText('dailyAmount');
  const amountToBeLoadedText = useText('amountToBeLoaded2');
  const referenceNumberText = useText('referenceNumber');
  const uploadingEllipsisText = useText('uploadingEllipsis');

  const { currentLoadBalance } = useSelector((state) => state?.balanceUpload);

  const [form] = Form.useForm();

  const [isCardNumberDisabled, setIsCardNumberDisabled] = useState(false);
  const [isRefNoDisabled, setIsRefNoDisabled] = useState(false);

  const handleClose = useCallback(
    (e) => {
      e.preventDefault();
      handleModalVisible(false);
    },
    [handleModalVisible],
  );

  const vouId = useMemo(() => {
    if (isMarket) return VouIdCode.Market;
    if (isCompliments) return VouIdCode.Compliments;
    return VouIdCode.Restaurant;
  }, [isMarket, isCompliments]);

  const deleteColumns = useMemo(() => {
    if (!decodedExcelFile?.current?.length) return [];

    return Object.keys(decodedExcelFile.current[0])
      .filter((item) => item !== 'rowNumber')
      .map((item) => {
        return {
          title: item,
          dataIndex: item,
          key: item,
          width: 100,
          align: 'center',
          ellipsis: true,
        };
      });
  }, [decodedExcelFile]);

  const columns = useMemo(() => {
    return decodedExcelFile?.current?.length
      ? Object.keys(decodedExcelFile.current[0])
          .filter((item) => item !== 'rowNumber')
          .map((item) => {
            return {
              label: item,
              id: alphabets.indexOf(item),
            };
          })
      : [];
  }, [decodedExcelFile]);

  const checkCardNumberAndRefNo = () => {
    const cardNumber = form.getFieldValue('CardNumberColumn');
    const refNo = form.getFieldValue('RefNoColumn');

    if (cardNumber) {
      form.setFieldsValue({ RefNoColumn: null });
      setIsRefNoDisabled(true);
      return;
    }

    if (refNo) {
      form.setFieldsValue({ CardNumberColumn: null });
      setIsCardNumberDisabled(true);
      return;
    }

    setIsCardNumberDisabled(false);
    setIsRefNoDisabled(false);
  };

  const onFinish = useCallback(
    async (values) => {
      const params = {
        FormFile: excelFile?.current,
        FirstRowHeaderColumn: values?.FirstRowHeaderColumn,
        UseDefaultTemplate: false,
        PeriodType: currentLoadBalance?.periodType,
        RefNoColumn: values?.RefNoColumn,
        DayCountColumn: values?.DayCountColumn,
        PriceColumn: values?.PriceColumn,
        CardNumberColumn: values?.CardNumberColumn,
        vouId: vouId,
      };

      excelParams.current = params;

      await dispatch(
        setSpinText(
          `${excelFile.current.name} <br/> <span style="color: #00a483">${uploadingEllipsisText}</span>`,
        ),
      );

      await dispatch(setShowLoader(true));

      try {
        await dispatch(
          excelUploadPrimaryColumn({
            dailyColumn: alphabets[values.DayCountColumn],
            priceColumn: alphabets[values.PriceColumn],
            FirstRowHeaderColumn: values.FirstRowHeaderColumn,
            type: values.CardNumberColumn ? 'cardNumber' : 'refNo',
            column: alphabets[values.CardNumberColumn] ?? alphabets[values.RefNoColumn],
            periodType: currentLoadBalance?.periodType,
          }),
        );

        await dispatch(loadBalanceDetailExcelUpload(params));

        handleModalVisible(false);

        await afterUpload();
      } finally {
        await dispatch(setSpinText(''));
        await dispatch(setShowLoader(false));
      }
    },
    [dispatch, handleClose, currentLoadBalance, excelFile, excelParams, vouId, handleModalVisible, afterUpload],
  );

  const chooseComplimentPriceLabel = () => `${monthlyAmountText}:`;

  const chooseLoadBalancePriceLabel = (periodType) => {
    if (periodType === 0) return `${dailyAmountText}:`;
    return `${amountToBeLoadedText}:`;
  };

  const choosePriceRender = isCompliments
    ? chooseComplimentPriceLabel()
    : chooseLoadBalancePriceLabel(currentLoadBalance?.periodType);

  const renderReferenceNumber = () => {
    return (
      <CustomFormItem
        label={
          <div style={isRefNoDisabled ? { color: 'rgb(180,180,180)' } : null}>
            {referenceNumberText}:
          </div>
        }
        name="RefNoColumn"
        className="order-month-form-item"
        rules={[{ required: !isRefNoDisabled, message: <Text t="requiredField2" /> }]}
      >
        <CustomSelect
          id="ticketRestaurant-balanceUpload-balanceInformation-excelUpload-refNo-select"
          placeholder={chooseText}
          height={36}
          disabled={isRefNoDisabled}
          onChange={checkCardNumberAndRefNo}
        >
          <Option value={''}>
            <Text t="choose" />
          </Option>
          {columns.map((colType) => (
            <Option
              id={`ticketRestaurant-balanceUpload-balanceInformation-excelUpload-refNo-${colType.label}-option`}
              key={colType.label}
              value={colType.id}
            >
              {colType.label}
            </Option>
          ))}
        </CustomSelect>
      </CustomFormItem>
    );
  };

  const renderCardNumber = () => {
    return (
      <CustomFormItem
        label={
          <div style={isCardNumberDisabled ? { color: 'rgb(180,180,180)' } : null}>
            {<Text t="cardNumber2" />}:
          </div>
        }
        name="CardNumberColumn"
        className="card-number-form-item"
        rules={[
          {
            required: !isCardNumberDisabled,
            message: <Text t="requiredField2" />,
          },
        ]}
      >
        <CustomSelect
          id="ticketRestaurant-balanceUpload-balanceInformation-excelUpload-cardNumber-select"
          placeholder={chooseText}
          height={36}
          onChange={checkCardNumberAndRefNo}
          disabled={isCardNumberDisabled}
        >
          <Option value={''}>
            <Text t="choose" />
          </Option>
          {columns.map((colType) => (
            <Option
              id={`ticketRestaurant-balanceUpload-balanceInformation-excelUpload-cardNumber-${colType.label}-option`}
              key={colType.label}
              value={colType.id}
            >
              {colType.label}
            </Option>
          ))}
        </CustomSelect>
      </CustomFormItem>
    );
  };

  if (!modalVisible) return null;

  return (
    <CustomModal
      className="excel-upload-delete-modal"
      width={1000}
      maskClosable={false}
      footer={false}
      title={<Text t="warning2" />}
      visible={modalVisible}
      onCancel={handleClose}
      closeIcon={<CustomImage src={modalClose} />}
    >
      <div className="excel-upload-card">
        <div className="my-template-excel-upload-container-inputs">
          <CustomForm
            name="excelUploadForm"
            className="excel-upload-form"
            autoComplete="off"
            layout={'horizontal'}
            form={form}
            onFinish={onFinish}
            style={{ marginTop: isCompliments ? 0 : '1rem' }}
          >
            <div className="summary-info-container">
              <div className="summary-info-content">
                <div className="summary-info">
                  <div
                    className={'summary-text'}
                    style={{
                      backgroundColor: 'white',
                      marginLeft: isCompliments ? 0 : 24,
                      paddingLeft: isCompliments ? 0 : 16,
                    }}
                  >
                    <CustomImage src={infoButton} className="error-title-icon" />
                    <span>
                      <Text t="selectTheCardNumberOrReferenceNumberAnd2" />{' '}
                      <b style={{ fontFamily: 'UbuntuBold, sans-serif' }}>
                        <Text t="loadCardInformation" />
                      </b>{' '}
                      <Text t="yourExcelWillBeUploadedAfterPressingThe" />
                    </span>
                  </div>
                  <div className="form-item-display">
                    <div>
                      <CustomFormItem
                        label={
                          <div>
                            <Text t="firstRowConsistsOfHeader2" />
                          </div>
                        }
                        name="FirstRowHeaderColumn"
                        height={36}
                        className="first-row-header-form-item"
                        rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                      >
                        <CustomSelect
                          height={36}
                          id="excel-upload-column-choice-first-row-select"
                          placeholder={chooseText}
                        >
                          <Option value={true}>
                            <Text t="yes" />
                          </Option>
                          <Option value={false}>
                            <Text t="no" />
                          </Option>
                        </CustomSelect>
                      </CustomFormItem>

                      {isCompliments ? renderReferenceNumber() : renderCardNumber()}
                    </div>
                    <div>
                      {!isCompliments ? renderReferenceNumber() : renderCardNumber()}

                      {currentLoadBalance?.periodType === 0 && (
                        <CustomFormItem
                          label={
                            <div>
                              <Text t="numberOfDays" />
                            </div>
                          }
                          name="DayCountColumn"
                          className="daily-count-form-item"
                          rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                        >
                          <CustomSelect
                            id="ticketRestaurant-balanceUpload-balanceInformation-excelUpload-dayCount-select"
                            height={36}
                            placeholder={chooseText}
                          >
                            {columns.map((colType) => (
                              <Option
                                id={`ticketRestaurant-balanceUpload-balanceInformation-excelUpload-dayCount-${colType.label}-option`}
                                key={colType.label}
                                value={colType.id}
                              >
                                {colType.label}
                              </Option>
                            ))}
                          </CustomSelect>
                        </CustomFormItem>
                      )}
                      <CustomFormItem
                        label={<div>{choosePriceRender}</div>}
                        height={36}
                        name="PriceColumn"
                        className="load-amount-form-item"
                        rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                      >
                        <CustomSelect
                          height={36}
                          id="ticketRestaurant-balanceUpload-balanceInformation-excelUpload-price-select"
                          placeholder={chooseText}
                        >
                          {columns.map((colType) => (
                            <Option
                              id={`ticketRestaurant-balanceUpload-balanceInformation-excelUpload-price-${colType.label}-option`}
                              key={colType.label}
                              value={colType.id}
                            >
                              {colType.label}
                            </Option>
                          ))}
                        </CustomSelect>
                      </CustomFormItem>
                    </div>
                  </div>
                  <div className="summary-list-content">
                    <Table
                      className="card-list-table"
                      dataSource={decodedExcelFile?.current}
                      columns={deleteColumns}
                      rowKey={(record) => JSON.stringify(record)}
                      pagination={false}
                      scroll={{ x: deleteColumns.length * 150, y: 200 }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div
              className="footer-btn"
              style={{ justifyContent: 'center', paddingTop: isCompliments ? 30 : '50px' }}
            >
              <button
                style={{
                  backgroundColor: 'transparent',
                  borderWidth: 0,
                  marginBottom: '25px',
                  cursor: 'pointer',
                }}
                onClick={handleClose}
              >
                <b>
                  <u>
                    <Text t="giveUp" />
                  </u>
                </b>
              </button>
              <CustomFormItem className="footer-form-item">
                <CustomButton
                  style={{
                    backgroundColor: '#f66301',
                    borderWidth: isCompliments ? 0 : 1,
                    height: isCompliments ? 46 : 58,
                  }}
                  className="submit-btn"
                  type="primary"
                  htmlType="submit"
                >
                  <span className="submit">
                    <Text t="loadCardInformation" />
                  </span>
                </CustomButton>
              </CustomFormItem>
            </div>
          </CustomForm>
        </div>
      </div>
    </CustomModal>
  );
};

ExcelUploadColumnChoice2.propTypes = {
  isCompliments: PropTypes.bool,
  isMarket: PropTypes.bool,
  modalVisible: PropTypes.any,
  handleModalVisible: PropTypes.any,
  excelFile: PropTypes.any,
  excelParams: PropTypes.any,
  afterUpload: PropTypes.any,
  decodedExcelFile: PropTypes.any,
};

export default ExcelUploadColumnChoice2;